import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Flame, 
  Star, 
  Trophy, 
  Target,
  Sparkles,
  Sun,
  Moon,
  Calendar,
  Award,
  Crown,
  Compass,
  Heart,
  Zap,
  Shield,
  Clock,
  TrendingUp,
  MapPin,
  Mountain,
  Footprints
} from "lucide-react";

// Badge definitions with unlock criteria - matches backend
export const BADGES = {
  // Streak badges
  first_checkin: {
    id: "first_checkin",
    name: "First Step",
    description: "Complete your first daily check-in",
    icon: Sparkles,
    color: "text-yellow-500",
    bgColor: "bg-yellow-500/20",
    category: "milestone",
    criteria: { type: "checkin_count", value: 1 }
  },
  streak_3: {
    id: "streak_3",
    name: "Building Momentum",
    description: "Maintain a 3-day streak",
    icon: Flame,
    color: "text-orange-400",
    bgColor: "bg-orange-400/20",
    category: "streak",
    criteria: { type: "streak", value: 3 }
  },
  streak_7: {
    id: "streak_7",
    name: "Weekly Warrior",
    description: "Maintain a 7-day streak",
    icon: Flame,
    color: "text-orange-500",
    bgColor: "bg-orange-500/20",
    category: "streak",
    criteria: { type: "streak", value: 7 }
  },
  streak_14: {
    id: "streak_14",
    name: "Fortnight Force",
    description: "Maintain a 14-day streak",
    icon: Flame,
    color: "text-red-400",
    bgColor: "bg-red-400/20",
    category: "streak",
    criteria: { type: "streak", value: 14 }
  },
  streak_21: {
    id: "streak_21",
    name: "Consistent Soul",
    description: "Maintain a 21-day streak",
    icon: Shield,
    color: "text-teal-500",
    bgColor: "bg-teal-500/20",
    category: "streak",
    criteria: { type: "streak", value: 21 }
  },
  streak_30: {
    id: "streak_30",
    name: "Monthly Master",
    description: "Maintain a 30-day streak",
    icon: Trophy,
    color: "text-amber-500",
    bgColor: "bg-amber-500/20",
    category: "streak",
    criteria: { type: "streak", value: 30 }
  },
  streak_90: {
    id: "streak_90",
    name: "Quarter Champion",
    description: "Maintain a 90-day streak",
    icon: Crown,
    color: "text-purple-500",
    bgColor: "bg-purple-500/20",
    category: "streak",
    criteria: { type: "streak", value: 90 }
  },
  
  // Phase badges (waitlist-triggered)
  phase_wake_up: {
    id: "phase_wake_up",
    name: "Awakened",
    description: "Join the Wake Up quest waitlist",
    icon: Sun,
    color: "text-yellow-400",
    bgColor: "bg-yellow-400/20",
    category: "phase",
    criteria: { type: "quest_waitlist", value: "wake_up" }
  },
  phase_burn_map: {
    id: "phase_burn_map",
    name: "Map Burner",
    description: "Join the Burn the Map quest waitlist",
    icon: Flame,
    color: "text-orange-400",
    bgColor: "bg-orange-400/20",
    category: "phase",
    criteria: { type: "quest_waitlist", value: "burn_the_map" }
  },
  phase_whole: {
    id: "phase_whole",
    name: "Made Whole",
    description: "Join the Whole quest waitlist",
    icon: Heart,
    color: "text-green-500",
    bgColor: "bg-green-500/20",
    category: "phase",
    criteria: { type: "quest_waitlist", value: "whole" }
  },
  phase_purposeful: {
    id: "phase_purposeful",
    name: "Purposeful",
    description: "Join the Purposeful Paths quest waitlist",
    icon: Compass,
    color: "text-blue-500",
    bgColor: "bg-blue-500/20",
    category: "phase",
    criteria: { type: "quest_waitlist", value: "purposeful_paths" }
  },
  
  // Journey completion
  journey_complete: {
    id: "journey_complete",
    name: "True North Found",
    description: "Join all 4 quest waitlists",
    icon: Star,
    color: "text-gold",
    bgColor: "bg-[#a89060]/20",
    category: "milestone",
    criteria: { type: "all_waitlists", value: true }
  },
  
  // Engagement badges
  journal_10: {
    id: "journal_10",
    name: "Reflector",
    description: "Write 10 journal entries",
    icon: Award,
    color: "text-blue-400",
    bgColor: "bg-blue-400/20",
    category: "engagement",
    criteria: { type: "journal_count", value: 10 }
  },
  reframe_5: {
    id: "reframe_5",
    name: "Deep Thinker",
    description: "Complete 5 thought reframes",
    icon: Award,
    color: "text-indigo-500",
    bgColor: "bg-indigo-500/20",
    category: "engagement",
    criteria: { type: "reframe_count", value: 5 }
  },
  
  // Time-based badges
  early_bird: {
    id: "early_bird",
    name: "Early Bird",
    description: "Check in before 8 AM",
    icon: Sun,
    color: "text-amber-400",
    bgColor: "bg-amber-400/20",
    category: "special",
    criteria: { type: "early_checkin", value: 1 }
  },
  night_owl: {
    id: "night_owl",
    name: "Night Owl",
    description: "Check in after 9 PM",
    icon: Moon,
    color: "text-indigo-400",
    bgColor: "bg-indigo-400/20",
    category: "special",
    criteria: { type: "late_checkin", value: 1 }
  },
  
  // Mini-Quest badges
  mini_quest_1: {
    id: "mini_quest_1",
    name: "First Flame",
    description: "Complete your first 7-day mini-quest",
    icon: Sparkles,
    color: "text-amber-500",
    bgColor: "bg-amber-500/20",
    category: "mini_quest",
    criteria: { type: "mini_quest_weeks", value: 1 }
  },
  mini_quest_4: {
    id: "mini_quest_4",
    name: "Pattern Breaker",
    description: "Complete 4 weekly mini-quests",
    icon: TrendingUp,
    color: "text-emerald-500",
    bgColor: "bg-emerald-500/20",
    category: "mini_quest",
    criteria: { type: "mini_quest_weeks", value: 4 }
  },
  mini_quest_10: {
    id: "mini_quest_10",
    name: "The Long Game",
    description: "Complete 10 mini-quests in any order",
    icon: Clock,
    color: "text-blue-500",
    bgColor: "bg-blue-500/20",
    category: "mini_quest",
    criteria: { type: "mini_quest_weeks", value: 10 }
  },
  mini_quest_13: {
    id: "mini_quest_13",
    name: "Depth Seeker",
    description: "Complete 13 weekly mini-quests",
    icon: Target,
    color: "text-purple-500",
    bgColor: "bg-purple-500/20",
    category: "mini_quest",
    criteria: { type: "mini_quest_weeks", value: 13 }
  },
  mini_quest_26: {
    id: "mini_quest_26",
    name: "Map Burner (Quest)",
    description: "Complete 26 weekly mini-quests",
    icon: MapPin,
    color: "text-orange-500",
    bgColor: "bg-orange-500/20",
    category: "mini_quest",
    criteria: { type: "mini_quest_weeks", value: 26 }
  },
  mini_quest_39: {
    id: "mini_quest_39",
    name: "Becoming Whole",
    description: "Complete 39 weekly mini-quests",
    icon: Heart,
    color: "text-pink-500",
    bgColor: "bg-pink-500/20",
    category: "mini_quest",
    criteria: { type: "mini_quest_weeks", value: 39 }
  },
  mini_quest_52: {
    id: "mini_quest_52",
    name: "Pathfinder",
    description: "Complete all 52 weekly mini-quests",
    icon: Mountain,
    color: "text-[#a69373]",
    bgColor: "bg-[#a69373]/20",
    category: "mini_quest",
    criteria: { type: "mini_quest_weeks", value: 52 }
  },
  mini_quest_streak_3: {
    id: "mini_quest_streak_3",
    name: "Consistent Flame",
    description: "Complete 3 mini-quests consecutively",
    icon: Flame,
    color: "text-red-500",
    bgColor: "bg-red-500/20",
    category: "mini_quest",
    criteria: { type: "mini_quest_streak", value: 3 }
  },
  mini_quest_deep_diver: {
    id: "mini_quest_deep_diver",
    name: "Deep Diver",
    description: "Write 200+ word responses for 7 consecutive days",
    icon: Footprints,
    color: "text-cyan-500",
    bgColor: "bg-cyan-500/20",
    category: "mini_quest",
    criteria: { type: "mini_quest_deep", value: 7 }
  }
};

// Badge component
export function Badge({ badge, earned = false, earnedAt = null, size = "md" }) {
  const BadgeIcon = badge.icon;
  
  const sizeClasses = {
    sm: "w-10 h-10",
    md: "w-14 h-14",
    lg: "w-20 h-20"
  };
  
  const iconSizes = {
    sm: "w-5 h-5",
    md: "w-7 h-7",
    lg: "w-10 h-10"
  };

  return (
    <div 
      className={`flex flex-col items-center gap-2 ${earned ? "" : "opacity-40 grayscale"}`}
      title={badge.description}
      data-testid={`badge-${badge.id}`}
    >
      <div className={`${sizeClasses[size]} rounded-full ${badge.bgColor} flex items-center justify-center relative`}>
        <BadgeIcon className={`${iconSizes[size]} ${badge.color}`} />
        {earned && (
          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
            <Zap className="w-2.5 h-2.5 text-white" />
          </div>
        )}
      </div>
      <span className={`text-xs font-medium text-center ${earned ? "text-[#a69373]" : "text-[#a69373]/50"}`}>
        {badge.name}
      </span>
    </div>
  );
}

// Badge grid display
export function BadgeGrid({ earnedBadges = [], category = null }) {
  const badges = category 
    ? Object.values(BADGES).filter(b => b.category === category)
    : Object.values(BADGES);

  return (
    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-4" data-testid="badge-grid">
      {badges.map(badge => (
        <Badge 
          key={badge.id}
          badge={badge}
          earned={earnedBadges.some(e => e.badge_id === badge.id)}
          earnedAt={earnedBadges.find(e => e.badge_id === badge.id)?.earned_at}
        />
      ))}
    </div>
  );
}

// Streak display component
export function StreakDisplay({ currentStreak = 0, longestStreak = 0, lastCheckIn = null }) {
  const isActive = lastCheckIn && isToday(new Date(lastCheckIn));
  
  function isToday(date) {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  return (
    <Card className="glass-card" data-testid="streak-display">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${isActive ? "bg-orange-500/20" : "bg-gray-200"} flex items-center justify-center`}>
              <Flame className={`w-6 h-6 ${isActive ? "text-orange-500" : "text-gray-400"}`} />
            </div>
            <div>
              <p className="text-2xl font-heading text-[#a69373]">{currentStreak}</p>
              <p className="text-xs text-[#a69373]/60">day streak</p>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-sm text-[#a69373]/60">Best: <span className="font-medium text-[#a69373]">{longestStreak} days</span></p>
            {!isActive && currentStreak > 0 && (
              <p className="text-xs text-orange-500 mt-1">Check in to keep your streak!</p>
            )}
          </div>
        </div>
        
        {/* Streak milestones */}
        <div className="flex gap-2 mt-4">
          {[3, 7, 14, 30, 90].map(milestone => (
            <div 
              key={milestone}
              className={`flex-1 h-2 rounded-full ${currentStreak >= milestone ? "bg-orange-400" : "bg-gray-200"}`}
              title={`${milestone} day streak`}
            />
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-[#a69373]/40 mt-1">
          <span>3</span>
          <span>7</span>
          <span>14</span>
          <span>30</span>
          <span>90</span>
        </div>
      </CardContent>
    </Card>
  );
}

// Badge unlock celebration
export function BadgeUnlockCelebration({ badge, onClose }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose?.();
    }, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const BadgeIcon = badge.icon;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center animate-fade-in"
      onClick={onClose}
      data-testid="badge-celebration"
    >
      <div 
        className="bg-white rounded-2xl p-8 text-center max-w-sm mx-4 animate-celebrate"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4">
          <div className={`w-20 h-20 mx-auto rounded-full ${badge.bgColor} flex items-center justify-center animate-pulse`}>
            <BadgeIcon className={`w-10 h-10 ${badge.color}`} />
          </div>
        </div>
        
        <h3 className="text-xl font-heading text-[#a69373] mb-2">Badge Unlocked!</h3>
        <p className="text-lg font-medium text-primary mb-1">{badge.name}</p>
        <p className="text-sm text-[#a69373]/60">{badge.description}</p>
        
        <button 
          onClick={onClose}
          className="mt-6 px-6 py-2 bg-primary text-white rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          Awesome!
        </button>
      </div>
    </div>
  );
}

export default Badge;
